import { useEffect } from "react";
import { createPortal } from "react-dom";

import { X } from "@/lib/icons";
import { useBboxOverlay } from "@/lib/useBboxOverlay";

type Props = {
  src: string | null;
  alt?: string;
  bbox?: string | null;
  onClose: () => void;
};

export function Lightbox({ src, alt = "", bbox, onClose }: Props) {
  const { imgRef, overlayStyle } = useBboxOverlay(bbox);
  useEffect(() => {
    if (!src) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [src, onClose]);

  if (!src) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-4 top-4 grid size-9 place-items-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
      >
        <X className="size-5" />
      </button>
      <div onClick={(e) => e.stopPropagation()} className="relative">
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className="block max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] rounded-md object-contain shadow-2xl"
        />
        {overlayStyle && (
          <div
            className="pointer-events-none absolute border-2 border-red-500"
            style={{ ...overlayStyle, boxShadow: "0 0 0 2px rgba(255,255,255,0.55)" }}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
